/* ============================================================
   HALAMAN LESSON  (/tutorial/:videoId)  — SESAAT Studio v3.9
   ============================================================
   Susunan diambil daripada laman belajar online yang biasa
   (Udemy, Coursera, Webflow University): kurikulum penuh melekat
   di tepi skrin, video di tengah, dan satu butang "Lesson
   seterusnya" di bawahnya.

   Rel kurikulum itu TIDAK ditulis di sini — ia milik
   [TutorialShell](./TutorialShell.jsx), rangka yang SAMA dipakai
   halaman Overview. Overview ialah item pertama dalam rel tersebut,
   jadi rel tidak pernah hilang sepanjang pelanggan berada dalam
   Tutorial.

   ⭐ KENAPA BUKAN MODAL LAGI
      Modal menutup dirinya dan mengembalikan pelanggan kepada grid
      — setiap video jadi satu perhentian, dan orang berhenti selepas
      satu. Rel menunjukkan dia berada di mana dan tinggal berapa
      banyak lagi, jadi bab dihabiskan dalam satu duduk.

   SUMBER KANDUNGAN = Firestore `config/tutorial`, SAMA dengan
   Overview. Halaman ini TIDAK menulis apa-apa ke Firestore;
   mengedit kandungan tetap di /tutorial (butang "Edit halaman").

   KOS: `loadTutorialCached` membaca cache sessionStorage, jadi
   berpindah antara lesson TIDAK menambah bacaan Firestore. Satu
   lawatan = satu bacaan, sama seperti sebelum ini.
   ============================================================ */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { getUserRole, canAccessAdmin } from '../utils/roles';
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, Circle, ListVideo, Pencil } from 'lucide-react';
import { klikBiasa } from '../utils/klikBiasa';
import {
    loadTutorial, loadTutorialCached, normalizeTutorial, youtubeEmbed,
    buildChapters, flattenChapters, alamatLesson, cariLessonDariAlamat,
    getWatched, markWatched, unmarkWatched,
    clearTutorialCache, TUTORIAL_DOC,
} from '../data/tutorial';

import '../design/tokens.css';
import { Badge, Btn, Card, CardBody, EmptyState } from '../design/ui';
import TutorialShell from './TutorialShell';
import TutorialArtikel from './TutorialArtikel';
import { GAYA_ARTIKEL, pautkanTeks } from './artikelKongsi';
import TutorialArtikelEditor from './TutorialArtikelEditor';

/* Siling lajur kandungan diikat pada TINGGI skrin, bukan nombor tetap.
   Bingkai video 16:9 tumbuh dengan lebar tetingkap, jadi tanpa ikatan
   ini ia menjadi lebih tinggi daripada skrin dan menolak nota serta
   butang lesson keluar pandangan. `(tinggi tersedia × 16 ÷ 9)` ialah
   lebar maksimum yang masih memuatkan video secara penuh; 360px itu
   ruang untuk tajuk di atas serta nota dan butang di bawah.

   Tajuk, nota dan butang berkongsi siling yang SAMA supaya tepinya
   sejajar dengan tepi video — kalau tidak, teks terkeluar lebih lebar
   daripada video dan blok itu nampak tidak sejajar. */
const LEBAR_UTAMA = 'min(100%, calc((100vh - 400px) * 16 / 9))';

/* ⭐ URL dalam nota jadi PAUTAN hidup (arahan user 2026-08-23). Takrifan
   `pautkanTeks` DIPINDAHKAN ke TutorialArtikel.jsx dan diimport di atas —
   artikel bergambar memakai peraturan pautan yang SAMA, dan dua salinan
   pasti terpesong. Nota reka bentuknya kekal di fail itu. */

/* Kutip SEMUA laluan gambar tutorial dalam satu salinan doc. Dipakai
   untuk membuang fail yatim: gambar yang ditukar, bloknya dibuang, atau
   dimuat naik lalu admin menekan Batal. Tanpa ini setiap suntingan
   meninggalkan fail yang tiada siapa rujuk dalam storage selama-lamanya. */
function kutipPathGambar(data) {
    const set = new Set();
    for (const v of data?.videos || []) {
        for (const b of v.blocks || []) {
            if (b.jenis === 'gambar' && b.path) set.add(b.path);
        }
    }
    return set;
}

/* ⛔ Hanya laluan di bawah `tutorial/` dipadam — pagar supaya path yang
   rosak/disunting tangan tidak boleh menyuruh kod ini memadam fail
   pelanggan. Kegagalan padam sengaja diabaikan (fail mungkin memang
   sudah tiada); ia tidak boleh menggagalkan simpanan. */
function padamFailYatim(paths) {
    for (const p of paths) {
        if (!p || !p.startsWith('tutorial/')) continue;
        deleteObject(storageRef(storage, p)).catch(() => { /* dah tiada */ });
    }
}

export default function TutorialLessonView() {
    const { videoId } = useParams();
    const navigate = useNavigate();
    const isAdmin = canAccessAdmin(getUserRole(auth.currentUser?.email));

    const [data, setData] = useState(() => normalizeTutorial(null));
    const [loading, setLoading] = useState(true);
    const [watched, setWatched] = useState(() => getWatched());
    const akar = useRef(null);

    /* ── Mod edit artikel (admin) ──────────────────────
       `draf` ialah salinan PENUH doc tutorial, bukan satu lesson: doc
       itu disimpan sebagai satu dokumen, jadi menyimpan separuh akan
       memadam kerja pada lesson lain. Null = tiada suntingan. */
    const [draf, setDraf] = useState(null);
    const [saving, setSaving] = useState(false);
    const [ralat, setRalat] = useState(null);
    /* Fail yang dimuat naik SEMASA sesi suntingan ini. Diperlukan supaya
       Batal boleh membersihkan gambar yang tidak jadi dipakai. */
    const failNaikRef = useRef(new Set());
    const editing = draf !== null;
    const view = editing ? draf : data;

    useEffect(() => {
        let hidup = true;
        (async () => {
            const dibaca = await loadTutorialCached();   // sudah fail-safe: pulangkan SEED bila gagal
            if (hidup) { setData(dibaca); setLoading(false); }
        })();
        return () => { hidup = false; };
    }, []);

    const chapters = useMemo(
        () => buildChapters(view, { onlyPlayable: !isAdmin }),
        [view, isAdmin]
    );
    const flat = useMemo(() => flattenChapters(chapters), [chapters]);

    /* Alamat → lesson. Menerima DUA bentuk:
         · baharu  /tutorial/<nama-chapter>-<kod>
         · lama    /tutorial/<id>            (link yang sudah dikongsi)
       Chapter dalam alamat mesti sepadan dengan chapter semasa lesson —
       kalau lesson sudah dipindah, alamat lama SENGAJA tidak dijumpai
       (keputusan user 2026-08-23). Lihat nota dalam data/tutorial.js. */
    const dijumpai = useMemo(() => cariLessonDariAlamat(videoId, view), [videoId, view]);
    /* Dicari semula dalam `flat` kerana senarai itu sudah ditapis mengikut
       siapa yang melihat — pelanggan tidak pernah dapat lesson tanpa video. */
    const idx = dijumpai ? flat.findIndex(v => v.id === dijumpai.id) : -1;
    const lesson = idx >= 0 ? flat[idx] : null;
    const prev = idx > 0 ? flat[idx - 1] : null;
    const next = idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null;
    const chapter = lesson ? (chapters.find(b => b.videos.some(v => v.id === lesson.id)) || null) : null;

    /* Tandakan ditonton sebaik video benar-benar dimuat (peristiwa
       `load` iframe), BUKAN dalam useEffect. Dua sebab: menulis state
       terus dalam effect mencetuskan render bertingkat (dilarang oleh
       lint projek), dan sauh yang betul memang video itu sendiri —
       lesson tanpa video tidak sepatutnya dikira ditonton.

       Semakan `has` menjaga butang "Tandakan belum ditonton": tanpa ia,
       setiap muat semula iframe akan menandakannya semula. */
    const tandaBilaVideoDimuat = () => {
        if (lesson?.id && !watched.has(lesson.id)) setWatched(markWatched(lesson.id));
    };

    /* Tukar lesson semasa berada di tengah halaman akan mendarat di
       tengah juga. Skrol berlaku DALAM ContentPanel, bukan pada
       window, jadi kita naik cari bekas yang benar-benar menatal. */
    useEffect(() => {
        let n = akar.current?.parentElement;
        while (n) {
            if (n.scrollHeight > n.clientHeight + 4) { n.scrollTop = 0; return; }
            n = n.parentElement;
        }
        window.scrollTo({ top: 0 });
    }, [videoId]);

    const toggleWatched = () => {
        if (!lesson) return;
        setWatched(watched.has(lesson.id) ? unmarkWatched(lesson.id) : markWatched(lesson.id));
    };

    /* ── Mod edit ──────────────────────────────────────
       ⛔ MASUK MOD EDIT WAJIB BACA SEGAR DARI FIRESTORE. Halaman ini
          biasanya memapar salinan cache sesi; kalau admin menyunting
          salinan itu, Simpan akan menulis keadaan LAMA ke atas kerja
          yang dibuat di tempat lain. Bacaan gagal = mod edit TIDAK
          dibuka langsung — menyunting SEED lalu menyimpannya akan
          memadam seluruh kandungan tutorial sebenar. */
    const mulaEdit = async () => {
        setRalat(null);
        try {
            const { data: segar } = await loadTutorial();
            failNaikRef.current = new Set();
            setData(segar);
            setDraf(JSON.parse(JSON.stringify(segar)));
        } catch (e) {
            console.error('[Tutorial] gagal baca sebelum edit:', e);
            setRalat('Tidak dapat membaca kandungan terkini. Muat semula halaman dan cuba lagi.');
        }
    };

    const batalEdit = () => {
        if (!window.confirm('Buang perubahan yang belum disimpan?')) return;
        const asal = kutipPathGambar(data);
        padamFailYatim([...failNaikRef.current].filter(p => !asal.has(p)));
        failNaikRef.current = new Set();
        setDraf(null);
        setRalat(null);
    };

    const simpanEdit = async () => {
        if (!draf) return;
        setSaving(true);
        setRalat(null);
        try {
            const bersih = normalizeTutorial(draf);
            await setDoc(doc(db, 'config', TUTORIAL_DOC), bersih);
            clearTutorialCache();

            /* Buang fail yang tidak lagi dirujuk: gambar yang ditukar atau
               bloknya dipadam (ada dalam doc LAMA), dan muat naik sesi ini
               yang akhirnya tidak dipakai. Dibuat SELEPAS tulisan berjaya —
               kalau tulisan gagal, fail masih diperlukan. */
            const pakai = kutipPathGambar(bersih);
            const buang = new Set();
            for (const p of kutipPathGambar(data)) if (!pakai.has(p)) buang.add(p);
            for (const p of failNaikRef.current) if (!pakai.has(p)) buang.add(p);
            padamFailYatim(buang);

            failNaikRef.current = new Set();
            setData(bersih);
            setDraf(null);
        } catch (e) {
            console.error('[Tutorial] simpan artikel gagal:', e);
            setRalat('Simpan gagal. Cuba lagi.');
        } finally {
            setSaving(false);
        }
    };

    /* Menyunting SATU lesson dalam salinan doc penuh. */
    const mutateLesson = (fn) => setDraf(d => {
        if (!d || !lesson) return d;
        const salin = JSON.parse(JSON.stringify(d));
        const v = salin.videos.find(x => x.id === lesson.id);
        if (v) {
            if (!Array.isArray(v.blocks)) v.blocks = [];
            fn(v);
        }
        return salin;
    });

    /* Rel kurikulum masih boleh diklik semasa mod edit — tetapi admin
       diberi amaran dahulu, kalau tidak satu klik cuai membuang kerja. */
    const bolehTinggalkanEdit = () =>
        !editing || window.confirm('Perubahan belum disimpan. Tinggalkan halaman ini?');

    const adaBlok = (lesson?.blocks?.length || 0) > 0;

    /* Butang terus ke tempat kerja yang baru diterangkan — cth lesson
       install → halaman Extension. PAUTAN sebenar, bukan butang: klik
       kanan / Ctrl+klik mesti boleh buka tab baharu (peraturan menu
       projek). Alamat luar (http…) dibuka dalam tab baharu; alamat
       dalaman berpindah tanpa muat semula penuh.
       Dihoist ke sini kerana ia dipakai oleh KEDUA-DUA rupa lesson
       (kad teks lama dan artikel bergambar). */
    const butangCta = (() => {
        if (!lesson?.ctaHref) return null;
        const luar = /^https?:\/\//i.test(lesson.ctaHref);
        return (
            <a
                href={lesson.ctaHref}
                target={luar ? '_blank' : undefined}
                rel={luar ? 'noopener noreferrer' : undefined}
                onClick={(e) => {
                    if (luar || !klikBiasa(e)) return;
                    e.preventDefault();
                    navigate(lesson.ctaHref);
                }}
                className="s-btn s-btn--primary"
                style={{ textDecoration: 'none' }}
            >
                {lesson.ctaLabel || 'Buka halaman'}
                <ArrowUpRight size={14} strokeWidth={2} />
            </a>
        );
    })();

    if (loading) {
        return (
            <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>
                <EmptyState title="Memuatkan lesson…">Sekejap sahaja.</EmptyState>
            </div>
        );
    }

    const done = !!lesson && watched.has(lesson.id);

    return (
        <TutorialShell
            akarRef={akar}
            chapters={chapters}
            data={view}
            watched={watched}
            aktif={lesson ? lesson.id : 'overview'}
            subtajuk={view.subtitle}
            bolehPergi={bolehTinggalkanEdit}
            tindakan={isAdmin && lesson ? (
                editing ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Btn onClick={simpanEdit} disabled={saving}>{saving ? 'Menyimpan…' : 'Simpan'}</Btn>
                        <Btn variant="ghost" onClick={batalEdit} disabled={saving}>Batal</Btn>
                    </div>
                ) : (
                    <Btn variant="ghost" icon={<Pencil size={14} strokeWidth={1.9} />} onClick={mulaEdit}>
                        Edit artikel
                    </Btn>
                )
            ) : null}
        >
            {!lesson ? (
                <EmptyState
                    title="Lesson ini tidak dijumpai"
                    action={<Btn icon={<ArrowLeft size={14} strokeWidth={1.9} />} onClick={() => navigate('/tutorial')}>Back to Overview</Btn>}
                >
                    Ia mungkin sudah dipadam atau videonya belum dimuat naik.
                </EmptyState>
            ) : (
                <div style={{ maxWidth: LEBAR_UTAMA }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                        <ListVideo size={14} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)' }} />
                        <span className="s-dim">
                            {chapter ? chapter.label : 'Tutorial'} · Lesson {idx + 1} of {flat.length}
                        </span>
                        {lesson.isNew && <Badge tone="brand">New</Badge>}
                    </div>

                    <h1 className="s-title" style={{ marginBottom: 16 }}>{lesson.title || 'Tanpa tajuk'}</h1>

                    <div style={{
                        aspectRatio: '16 / 9', borderRadius: 'var(--s-r-lg)', overflow: 'hidden',
                        border: '1px solid var(--s-line)',
                        /* Hitam hanya di belakang video sebenar. Petak hitam
                           kosong pada lesson tanpa link nampak seperti video
                           rosak; bingkai kosong bertema lebih jujur. */
                        background: lesson.youtubeId ? '#000' : 'var(--s-fill)',
                        display: 'grid', placeItems: 'center', marginBottom: 16,
                    }}>
                        {lesson.youtubeId ? (
                            <iframe
                                key={lesson.id}
                                src={youtubeEmbed(lesson.youtubeId)}
                                onLoad={tandaBilaVideoDimuat}
                                title={lesson.title || 'Video tutorial'}
                                style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        ) : (
                            /* Hanya admin boleh sampai ke sini — rel pelanggan
                               tidak pernah mengandungi lesson tanpa video. */
                            <span className="s-meta">Belum ada video — tampal link YouTube di halaman Overview.</span>
                        )}
                    </div>

                    {ralat && (
                        <div style={{
                            margin: '0 0 16px', padding: '12px 14px', borderRadius: 'var(--s-r)',
                            background: 'var(--s-fill)', borderLeft: '3px solid var(--s-stop)',
                            fontSize: 14, color: 'var(--s-ink-2)',
                        }}>{ralat}</div>
                    )}

                    {/* ⛔ TIGA RUPA, SATU LESSON — jangan gabungkan:
                          · mod edit          → editor blok
                          · ada blok artikel  → artikel bergambar
                          · tiada blok        → kad teks LAMA, tidak berubah
                                                walau satu piksel. Inilah yang
                                                menjaga setiap lesson sedia ada. */}
                    {editing ? (
                        <TutorialArtikelEditor
                            lesson={lesson}
                            mutate={mutateLesson}
                            jejakFail={(p) => failNaikRef.current.add(p)}
                        />
                    ) : adaBlok ? (
                        <>
                            <style>{GAYA_ARTIKEL}</style>
                            <TutorialArtikel lesson={lesson} />
                            {butangCta && <div style={{ marginTop: 22 }}>{butangCta}</div>}
                        </>
                    ) : (lesson.body || lesson.ctaHref) && (
                        <Card style={{ marginBottom: 16 }}>
                            <CardBody>
                                {/* ⭐ `pre-wrap` — teks lesson ditaip dalam textarea di halaman
                                    Overview, jadi baris baharu yang admin tekan MEMANG sebahagian
                                    daripada kandungan (senarai langkah, blok JSON contoh). Tanpa ia
                                    HTML meleburkan setiap baris baharu menjadi satu ruang dan seluruh
                                    lesson keluar sebagai SATU perenggan panjang (aduan user 2026-08-22).
                                    ⛔ Jangan "kemaskan" dengan merapatkan baris kosong berlebihan —
                                       user mahu teks keluar TEPAT seperti ditaip supaya ia boleh
                                       disalin semula bulat-bulat ke dalam agent. */}
                                {lesson.body && (
                                    <p className="s-meta" style={{ lineHeight: 1.65, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{pautkanTeks(lesson.body)}</p>
                                )}
                                {butangCta && <div style={{ marginTop: lesson.body ? 14 : 0 }}>{butangCta}</div>}
                            </CardBody>
                        </Card>
                    )}

                    {/* Navigasi lesson disorok semasa mod edit — satu klik
                        cuai di situ membuang suntingan yang belum disimpan. */}
                    <div style={{
                        display: editing ? 'none' : 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        borderTop: '1px solid var(--s-line)', paddingTop: 16, marginTop: 16,
                    }}>
                        <Btn
                            variant={done ? 'primary' : 'ghost'}
                            icon={done ? <Check size={14} strokeWidth={2.4} /> : <Circle size={14} strokeWidth={1.9} />}
                            onClick={toggleWatched}
                        >
                            {done ? 'Watched' : 'Mark as watched'}
                        </Btn>

                        <span style={{ flex: 1 }} />

                        {prev && (
                            <Btn variant="ghost" icon={<ArrowLeft size={14} strokeWidth={1.9} />} onClick={() => navigate(alamatLesson(prev, data))}>
                                Previous lesson
                            </Btn>
                        )}
                        {next ? (
                            <Btn onClick={() => navigate(alamatLesson(next, data))}>
                                Next lesson <ArrowRight size={14} strokeWidth={1.9} style={{ marginLeft: 4, verticalAlign: -2 }} />
                            </Btn>
                        ) : (
                            <Btn onClick={() => navigate('/tutorial')}>Back to Overview</Btn>
                        )}
                    </div>
                </div>
            )}
        </TutorialShell>
    );
}
